import React, { useState } from 'react';
import axios from 'axios';
import { ShieldAlert, Send, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUREAUS = ['Experian', 'Equifax', 'TransUnion'];

const SEVERITY_COLOR = {
  High: { color: '#f87171', bg: 'rgba(248, 113, 113, 0.12)' },
  Medium: { color: '#fb923c', bg: 'rgba(251, 146, 60, 0.12)' },
  Low: { color: '#ffd76a', bg: 'rgba(255, 215, 106, 0.12)' },
};

const DisputePanel = ({ item, token }) => {
  const items = item.negativeItems || [];
  const [selectedIds, setSelectedIds] = useState([]);
  const [bureau, setBureau] = useState('Experian');
  const [busy, setBusy] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [letterPreview, setLetterPreview] = useState('');
  const [addressForm, setAddressForm] = useState({
    line1: '', line2: '', city: '', state: '', zip: '',
  });

  if (items.length === 0) {
    return (
      <div className="admin-disputes-empty" data-testid={`disputes-empty-${item.id}`}>
        <CheckCircle2 size={16} /> No negative items detected — nothing to dispute.
      </div>
    );
  }

  const toggle = (id) => {
    setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };

  const callApi = async (path, body, action) => {
    setBusy(action);
    setFeedback(null);
    try {
      const res = await axios.post(`${API}${path}`, { ...body, token });
      return res.data;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Request failed';
      setFeedback({ kind: 'error', msg });
      return null;
    } finally {
      setBusy(null);
    }
  };

  const generate = async () => {
    if (selectedIds.length === 0) {
      setFeedback({ kind: 'error', msg: 'Select at least one item to dispute.' });
      return;
    }
    const data = await callApi('/admin/disputes/generate-letter', {
      qualificationId: item.id,
      bureau,
      itemIds: selectedIds,
    }, 'generate');
    if (data?.letterText) {
      setLetterPreview(data.letterText);
      setFeedback({ kind: 'ok', msg: `Letter generated for ${bureau} (${data.itemCount} items).` });
    }
  };

  const mail = async () => {
    if (selectedIds.length === 0) {
      setFeedback({ kind: 'error', msg: 'Select at least one item to mail.' });
      return;
    }
    if (!addressForm.line1 || !addressForm.city || !addressForm.state || !addressForm.zip) {
      setFeedback({ kind: 'error', msg: 'Client mailing address (line1, city, state, zip) is required.' });
      return;
    }
    const data = await callApi('/admin/disputes/mail-letter', {
      qualificationId: item.id,
      bureau,
      itemIds: selectedIds,
      clientAddressLine1: addressForm.line1,
      clientAddressLine2: addressForm.line2 || null,
      clientCity: addressForm.city,
      clientState: addressForm.state,
      clientZip: addressForm.zip,
    }, 'mail');
    if (data?.success) {
      const lob = data.lob || {};
      setFeedback({
        kind: lob.mocked ? 'warn' : 'ok',
        msg: lob.mocked
          ? `MOCKED: ${lob.message || 'Lob API key missing — letter not actually mailed.'}`
          : `Mailed to ${bureau} via Lob Certified. Tracking ID: ${lob.id || 'n/a'}`,
      });
    }
  };

  const syncGhl = async () => {
    const data = await callApi('/admin/disputes/sync-ghl', { qualificationId: item.id }, 'ghl');
    if (data?.success) {
      setFeedback({
        kind: data.mocked ? 'warn' : 'ok',
        msg: data.mocked
          ? `MOCKED: ${data.message || 'GHL API key missing — sync skipped.'}`
          : `Synced to GoHighLevel. Contact ID: ${data.contactId}`,
      });
    }
  };

  return (
    <div className="admin-disputes" data-testid={`disputes-panel-${item.id}`}>
      <div className="admin-disputes-head">
        <ShieldAlert size={16} />
        <span>Dispute Juggernaut · {items.length} negative item{items.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="admin-disputes-items">
        {items.map((n) => {
          const sev = SEVERITY_COLOR[n.severity] || SEVERITY_COLOR.Medium;
          const checked = selectedIds.includes(n.id);
          return (
            <label
              key={n.id}
              className={`admin-dispute-item ${checked ? 'admin-dispute-item-checked' : ''}`}
              data-testid={`dispute-item-${item.id}-${n.id}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(n.id)}
              />
              <div className="admin-dispute-item-body">
                <div className="admin-dispute-item-top">
                  <strong>{n.type}</strong>
                  <span className="admin-dispute-item-sev" style={{ background: sev.bg, color: sev.color }}>
                    {n.severity}
                  </span>
                </div>
                <div className="admin-dispute-item-meta">
                  {n.creditorName} · {n.accountNumber || '—'} · ${(n.amount || 0).toLocaleString()} · {n.dateReported || '—'}
                </div>
                <div className="admin-dispute-item-strategy">{n.suggestedStrategy}</div>
                <div className="admin-dispute-item-bureaus">
                  Reported on: {(n.bureausReporting || []).join(', ') || '—'} · Reason: <em>{n.disputeReason}</em>
                </div>
              </div>
            </label>
          );
        })}
      </div>

      <div className="admin-disputes-controls">
        <label className="admin-dispute-field">
          <span>Send to:</span>
          <select value={bureau} onChange={(e) => setBureau(e.target.value)} data-testid={`dispute-bureau-${item.id}`}>
            {BUREAUS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        <button
          type="button"
          className="admin-dispute-btn admin-dispute-btn-secondary"
          onClick={generate}
          disabled={busy === 'generate'}
          data-testid={`dispute-generate-${item.id}`}
        >
          {busy === 'generate' ? <Loader2 size={14} className="admin-spin" /> : <RefreshCw size={14} />}
          Generate Letter
        </button>
      </div>

      <details className="admin-disputes-address">
        <summary>Client Mailing Address (required to mail)</summary>
        <div className="admin-disputes-address-grid">
          <input placeholder="Address Line 1" value={addressForm.line1} onChange={(e) => setAddressForm({ ...addressForm, line1: e.target.value })} data-testid={`addr-line1-${item.id}`} />
          <input placeholder="Line 2 (optional)" value={addressForm.line2} onChange={(e) => setAddressForm({ ...addressForm, line2: e.target.value })} />
          <input placeholder="City" value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} data-testid={`addr-city-${item.id}`} />
          <input placeholder="State" value={addressForm.state} onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })} maxLength={2} data-testid={`addr-state-${item.id}`} />
          <input placeholder="ZIP" value={addressForm.zip} onChange={(e) => setAddressForm({ ...addressForm, zip: e.target.value })} data-testid={`addr-zip-${item.id}`} />
        </div>
      </details>

      <div className="admin-disputes-actions">
        <button
          type="button"
          className="admin-dispute-btn admin-dispute-btn-blast"
          onClick={async () => {
            if (!addressForm.line1 || !addressForm.city || !addressForm.state || !addressForm.zip) {
              setFeedback({ kind: 'error', msg: 'Client mailing address (line1, city, state, zip) is required to blast all bureaus.' });
              return;
            }
            if (!window.confirm(`This will generate AND mail dispute letters to ALL 3 bureaus for every negative item on this report (${items.length} items). Continue?`)) return;
            const data = await callApi('/admin/disputes/blast-all', {
              qualificationId: item.id,
              clientAddressLine1: addressForm.line1,
              clientAddressLine2: addressForm.line2 || null,
              clientCity: addressForm.city,
              clientState: addressForm.state,
              clientZip: addressForm.zip,
            }, 'blast');
            if (data?.success) {
              const errors = (data.results || []).filter((r) => r.error);
              const mocked = (data.results || []).some((r) => r.lob && r.lob.mocked);
              if (errors.length) {
                setFeedback({ kind: 'error', msg: `Blast partial: ${data.bureausMailed} bureau(s) mailed; ${errors.length} failed — ${errors[0]?.error || ''}` });
              } else if (mocked) {
                setFeedback({ kind: 'warn', msg: `BLAST (MOCK MODE): ${data.bureausMailed}/3 bureaus queued — Lob key missing or unverified. Letters NOT mailed.` });
              } else {
                setFeedback({ kind: 'ok', msg: `🚀 BLAST sent: ${data.bureausMailed}/3 bureaus mailed certified for ${data.totalItems} item${data.totalItems !== 1 ? 's' : ''}.` });
              }
            }
          }}
          disabled={busy === 'blast'}
          data-testid={`dispute-blast-${item.id}`}
        >
          {busy === 'blast' ? <Loader2 size={14} className="admin-spin" /> : <ShieldAlert size={14} />}
          BLAST ALL · 3 Bureaus
        </button>
        <button
          type="button"
          className="admin-dispute-btn admin-dispute-btn-primary"
          onClick={mail}
          disabled={busy === 'mail'}
          data-testid={`dispute-mail-${item.id}`}
        >
          {busy === 'mail' ? <Loader2 size={14} className="admin-spin" /> : <Mail size={14} />}
          Mail Selected
        </button>
        <button
          type="button"
          className="admin-dispute-btn admin-dispute-btn-ghost"
          onClick={syncGhl}
          disabled={busy === 'ghl'}
          data-testid={`dispute-ghl-${item.id}`}
        >
          {busy === 'ghl' ? <Loader2 size={14} className="admin-spin" /> : <Send size={14} />}
          Sync to GoHighLevel
        </button>
      </div>

      {feedback && (
        <div
          className={`admin-disputes-feedback admin-disputes-feedback-${feedback.kind}`}
          data-testid={`disputes-feedback-${item.id}`}
        >
          {feedback.kind === 'ok' ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
          {feedback.msg}
        </div>
      )}

      {letterPreview && (
        <details className="admin-disputes-preview" data-testid={`disputes-preview-${item.id}`}>
          <summary>Generated Letter Preview</summary>
          <pre>{letterPreview}</pre>
        </details>
      )}
    </div>
  );
};

export default DisputePanel;
